import { NumberField } from '@automata/ui';
import type { DriverArgument } from '@automata/wfb-api';
import type { SxProps } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { UnitOfMeasurementSelect } from './UnitOfMeasurementSelect';
import { paramToString } from './utils';
import { selectDriverArgumentsByTaskProperties } from '../../../../features/workflow/selectors';
import { removeTaskParameter } from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import type { TaskProperties, UpdateTaskParametersFn } from '../../../../types';

export type SpeedInputSelectProps = {
  error?: boolean;
  label: string;
  parameter: DriverArgument;
  parameterValue: string;
  taskProperties: TaskProperties;
  updateTaskParameters: UpdateTaskParametersFn;
  handleChangeSpeedUnit: (unit: 'g_force' | 'speed') => void;
};

const sx: SxProps = {
  width: '100%',
  flex: 1,
};

export const SpeedInputSelect = ({
  error,
  label,
  parameter,
  parameterValue,
  taskProperties,
  updateTaskParameters,
  handleChangeSpeedUnit,
}: SpeedInputSelectProps) => {
  const dispatch = useAppDispatch();
  const driverArguments = useAppSelector(
    selectDriverArgumentsByTaskProperties(taskProperties)
  );
  const [speedUnit, setSpeedUnit] = useState(
    taskProperties?.taskParameters?.g_force ? 'g' : 'rpm'
  );
  // speedParameterName is used to determine which parameter to update
  const speedParameterName = speedUnit === 'rpm' ? 'speed' : 'g_force';

  // if the speed unit is changed, reset the previous parameter value
  useEffect(() => {
    handleChangeSpeedUnit(speedParameterName);
    if (speedParameterName === 'g_force') {
      dispatch(
        removeTaskParameter({ id: taskProperties.id, fieldName: 'speed' })
      );

      updateTaskParameters('radius_mm', '150');
    }

    if (speedParameterName === 'speed') {
      dispatch(
        removeTaskParameter({ id: taskProperties.id, fieldName: 'g_force' })
      );
      dispatch(
        removeTaskParameter({ id: taskProperties.id, fieldName: 'radius_mm' })
      );
    }
  }, [
    dispatch,
    handleChangeSpeedUnit,
    speedParameterName,
    taskProperties.id,
    updateTaskParameters,
  ]);

  const handleSpeed = useCallback(
    (e: number | null) => {
      updateTaskParameters(speedParameterName, paramToString(e));
    },
    [speedParameterName, updateTaskParameters]
  );

  const speedMin =
    speedParameterName === 'speed'
      ? parameter.min_value
      : driverArguments?.g_force?.min_value;
  const speedMax =
    speedParameterName === 'speed'
      ? parameter.max_value
      : driverArguments?.g_force?.max_value;

  const currentParameterValue =
    speedParameterName === 'speed'
      ? parameterValue
      : (taskProperties.taskParameters?.g_force as string);

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
      <NumberField
        fullWidth
        decimal={parameter.type === 'float'}
        error={error}
        label={label}
        min={speedMin ?? undefined}
        max={speedMax ?? undefined}
        sx={sx}
        value={currentParameterValue}
        onChange={handleSpeed}
      />
      <UnitOfMeasurementSelect
        value={speedUnit}
        units={['rpm', 'g']}
        onChange={setSpeedUnit}
      />
    </div>
  );
};
